import { match } from 'ts-pattern'
import React from 'react'
import { ReactComponent as IconMobile } from './../../../images/icons/icon_mobile.svg'
import { ReactComponent as IconSource } from './../../../images/icons/icon_source.svg'

import { ReactComponent as PerformanceCPUserPathEnd } from './../../../images/performance/performance-cp-user-end.svg'
import { ReactComponent as PerformanceCPUserPageView } from './../../../images/performance/performance-cp-user-page-view.svg'
import classNames from 'classnames'
import { Pill } from '@demandlocal/components'

export type UserPathStepProps = {
  Device: 'Mobile' | 'Tablet' | 'Desktop' | 'PC' | 'ConnectedTV'
  Campaign: string
  'Ad Group': string
  Audience: string
  'Event Type': 'Conversion' | undefined
  Site: string
  Time: string
  Tag?: string
  Type?: string
}

export default function UserPathStep({ path }: { path: UserPathStepProps }) {
  const device = match(path.Device)
    .with('Mobile', () => ({
      text: 'Mobile',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
    .with('Tablet', () => ({
      text: 'Tablet',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
    .with('Desktop', () => ({
      text: 'Desktop',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
    .with('PC', () => ({
      text: 'PC',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
    .with('ConnectedTV', () => ({
      text: 'ConnectedTV',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
    .otherwise(() => ({
      text: 'Unknown',
      icon: <IconMobile className={'fill-transparent stroke-white/40'} />,
    }))
  if (path['Event Type'] === 'Conversion') {
    const url = () => {
      try {
        return new URL(path.Site)?.hostname
      } catch (e) {
        return ''
      }
    }
    const isAPV = path?.['Conversion Sub Category']?.toLowerCase()?.includes('apv')
    return (
      <div
        className={classNames('p-3 rounded-2xl flex-col gap-2 w-80 h-80 flex border-2', {
          'bg-sunglow/30 border-sunglow/50': isAPV,
          'bg-tea-green border-green-primary': !isAPV,
        })}>
        <div className={'flex w-full items-center flex-col gap-2'}>
          {isAPV ? <PerformanceCPUserPageView /> : <PerformanceCPUserPathEnd />}
          <div className={'mx-auto text-center'}>
            <h3
              className={classNames('font-bold text-sm', {
                'text-secondary-field-drab': isAPV,
                'text-green-primary': !isAPV,
              })}>
              {isAPV ? 'PageView' : 'Conversion'}
            </h3>
            <h4
              className={classNames('font-regular text-xs', {
                'text-secondary-field-drab': isAPV,
                'text-green-primary': !isAPV,
              })}>
              {path?.Time}
            </h4>
          </div>
          <div
            className={classNames(
              'font-bold text-xs rounded-sm p-2 flex flex-row gap-2 items-center w-full line-clamp-1',
              {
                'text-secondary-field-drab bg-sunglow/50': isAPV,
                'bg-green-primary/20 text-green-primary': !isAPV,
              },
            )}>
            <IconSource
              className={classNames({
                'stroke-green-primary': !isAPV,
                'stroke-secondary-field-drab': isAPV,
              })}
            />
            {url()}
          </div>
          <div
            className={classNames(
              'font-bold text-xs rounded-sm p-2 flex flex-row gap-2 items-center w-full line-clamp-1',
              {
                'text-secondary-field-drab bg-sunglow/50': isAPV,
                'bg-green-primary/20 text-green-primary': !isAPV,
              },
            )}>
            <IconMobile
              className={classNames({
                'stroke-green-primary fill-green-primary/20': !isAPV,
                'stroke-secondary-field-drab fill-secondary-field-drab/20': isAPV,
              })}
            />
            {path?.['Conversion Sub Category']}
          </div>
        </div>
        <span
          className={classNames('text-xs break-words font-semibold line-clamp-4', {
            ' text-green-primary': !isAPV,
            ' text-secondary-field-drab': isAPV,
          })}>
          {path?.Site}
        </span>
      </div>
    )
  }

  return (
    <div className={'bg-primary-xiketic p-3 rounded-2xl w-80 h-80 flex flex-col gap-2'}>
      {/* eslint-disable-next-line react/prop-types */}
      <div className="flex flex-row justify-between items-center">
        <div className={'text-gainsboro-gray font-bold text-xs'}>{path?.Time}</div>
        {path['Event Type'] && (
          <Pill type={path['Event Type'] === 'Click' ? 'error' : 'success'}>
            {path['Event Type']}
          </Pill>
        )}
        {path.Type === 'click' && <Pill type={'success'}>Click</Pill>}
      </div>
      <div
        className={
          'bg-blue-aero/30 text-white font-bold text-xs rounded-sm p-2 flex flex-row gap-2 items-center'
        }>
        <IconSource className={'stroke-blue-aero'} />
        {path.Site}
      </div>
      <div
        className={
          'bg-primary-independence text-white font-bold text-xs rounded-sm p-2 flex flex-row gap-2 items-center'
        }>
        {device.icon}
        {device.text}
      </div>
      <div className={'space-y-1 mt-2 pb-2 border-b-2 border-b-davy-gray'}>
        <h3 className={'text-white font-bold text-xs uppercase'}>Campaign:</h3>
        <h4 className={'text-white/70 text-xs line-clamp-1'}>{path.Campaign}</h4>
      </div>
      <div className={'space-y-1 mt-2 pb-2 border-b-2 border-b-davy-gray'}>
        <h3 className={'text-white font-bold text-xs uppercase'}>Ad Group:</h3>
        <h4 className={'text-white/70 text-xs line-clamp-1'}>
          {path['Ad Group'] ?? '-'}
        </h4>
      </div>
      <div className={'space-y-1 mt-2 pb-2'}>
        <h3 className={'text-white font-bold text-xs uppercase'}>Audience:</h3>
        <h4 className={'text-white/70 text-xs line-clamp-1'}>{path.Audience ?? '-'}</h4>
      </div>
    </div>
  )
}
